// Confirms an onboarding save from the PUT /organizations/{id} response.
// The API commits the write, reads the row back from Spanner and returns it,
// so the response is proof the save landed. Using it lets a stage advance
// right away instead of waiting on refetches (which can lag behind the write).

import type { Organization } from '../../../../types/organization.types';

type JsonObject = Record<string, unknown>;

export interface OrganizationSaveResponse {
  success?: boolean;
  organization?: JsonObject | null;
}

/** Store fields the onboarding stages write, with the same name in the API response. */
const MIRRORED_FIELDS = [
  'org_name',
  'org_phone',
  'email',
  'notification_email',
  'client_address',
  'timezone',
  'remi_intro_name',
  'assistant_name',
  'assistant_name_pronunciation',
  'call_settings',
] as const;

export interface ConfirmedSave {
  /** Values to merge into the organization in the store. */
  patch: Partial<Organization>;
}

const asObject = (value: unknown): JsonObject | undefined => {
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? (parsed as JsonObject) : undefined;
    } catch {
      return undefined;
    }
  }
  return value && typeof value === 'object' ? (value as JsonObject) : undefined;
};

/**
 * Returns the store patch when the response shows the save landed (per
 * `isConfirmed`), otherwise null so the caller can keep the user on the stage.
 */
export const getConfirmedSave = (
  response: OrganizationSaveResponse | null | undefined,
  isConfirmed: (onboardingSetup: JsonObject, onboardingData: JsonObject) => boolean
): ConfirmedSave | null => {
  if (!response?.success || !response.organization) return null;
  const org = response.organization;
  const onboardingSetup = asObject(org.onboarding_setup) ?? {};
  const onboardingData = asObject(org.onboarding_data) ?? {};
  if (!isConfirmed(onboardingSetup, onboardingData)) return null;

  const patch: Partial<Organization> = {
    onboardingSetup: onboardingSetup as Organization['onboardingSetup'],
    onboardingData: onboardingData as Organization['onboardingData'],
  };
  for (const field of MIRRORED_FIELDS) {
    if (field in org) (patch as JsonObject)[field] = org[field];
  }
  return { patch };
};

/** Confirmation rule for the numbered stages: `onboarding_setup.stageN.completed`. */
export const stageCompleted =
  (stageKey: `stage${number}`) =>
  (onboardingSetup: JsonObject): boolean =>
    asObject(onboardingSetup[stageKey])?.completed === true;
